import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

/** Advance a calendar date by whole days using UTC date math (no DST / 24h drift). */
export const addCalendarDays = (yyyyMmDd: string, deltaDays: number): string => {
  const [y, m, d] = yyyyMmDd.split('-').map(Number);
  const t = Date.UTC(y, m - 1, d + deltaDays);
  const dt = new Date(t);
  const yyyy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/** Inclusive count of calendar days from startYmd through endYmd. */
export const diffCalendarDaysInclusive = (startYmd: string, endYmd: string): number => {
  const [ys, ms, ds] = startYmd.split('-').map(Number);
  const [ye, me, de] = endYmd.split('-').map(Number);
  const startMs = Date.UTC(ys, ms - 1, ds);
  const endMs = Date.UTC(ye, me - 1, de);
  return Math.floor((endMs - startMs) / (1000 * 60 * 60 * 24)) + 1;
};

export type TournamentCalendarSpan = {
  startYmd: string;
  endYmd: string;
  dayCount: number;
};

export function getVenueLocalParts(iso: string | Date, timeZone: string): { date: string; time: string } {
  const date = new Date(iso);
  return {
    date: formatInTimeZone(date, timeZone, 'yyyy-MM-dd'),
    time: formatInTimeZone(date, timeZone, 'HH:mm'),
  };
}

export function getTournamentCalendarSpan(
  startIso: string,
  endIso: string,
  timeZone: string
): TournamentCalendarSpan {
  const startYmd = formatInTimeZone(new Date(startIso), timeZone, 'yyyy-MM-dd');
  const endYmd = formatInTimeZone(new Date(endIso), timeZone, 'yyyy-MM-dd');
  return {
    startYmd,
    endYmd,
    dayCount: Math.max(1, diffCalendarDaysInclusive(startYmd, endYmd)),
  };
}

/** Format a UTC instant for datetime-local inputs in the venue timezone. */
export function toDatetimeLocalValue(iso: string | Date | null | undefined, timeZone: string): string {
  if (!iso) return '';
  return formatInTimeZone(new Date(iso), timeZone, "yyyy-MM-dd'T'HH:mm");
}

/** Parse a datetime-local value as venue-local wall time and return UTC ISO. */
export function fromDatetimeLocalValue(localValue: string, timeZone: string): string {
  return fromZonedTime(localValue, timeZone).toISOString();
}

/** Keep wall-clock time in venue TZ but move to a new calendar date. */
export function reanchorInstantToCalendarDate(
  iso: string | Date,
  timeZone: string,
  targetYmd: string
): string {
  const clock = formatInTimeZone(new Date(iso), timeZone, 'HH:mm:ss');
  return fromZonedTime(`${targetYmd}T${clock}`, timeZone).toISOString();
}

/** Format a UTC instant for display in the venue timezone. */
export function formatInVenueTimeZone(
  iso: string | Date | null | undefined,
  timeZone: string,
  pattern: string
): string {
  if (!iso) return '';
  return formatInTimeZone(new Date(iso), timeZone, pattern);
}

export function formatVenueReadableDate(
  iso: string | Date | null | undefined,
  timeZone: string
): string {
  return formatInVenueTimeZone(iso, timeZone, 'EEEE dd MMM yyyy HH:mm');
}

export function formatVenueDateWithTime(
  iso: string | Date | null | undefined,
  timeZone: string
): string {
  return formatInVenueTimeZone(iso, timeZone, 'dd/MM/yyyy HH:mm');
}

export function formatVenuePublicDate(
  iso: string | Date | null | undefined,
  timeZone: string
): string {
  return formatInVenueTimeZone(iso, timeZone, "EEEE dd MMM yyyy 'at' HH:mm");
}

export function formatVenueDateOnly(
  iso: string | Date | null | undefined,
  timeZone: string
): string {
  return formatInVenueTimeZone(iso, timeZone, 'dd/MM yyyy');
}

const VENUE_DATETIME_LOCAL_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/;

/** True when value is a datetime-local wall-clock string (no Z or offset). */
export function isVenueDatetimeLocal(value: string): boolean {
  if (!value || value.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(value)) {
    return false;
  }
  return VENUE_DATETIME_LOCAL_PATTERN.test(value);
}

export type MatchDisplayDateFields = {
  scheduleDate?: string | Date | null;
  startDate?: string | Date | null;
};

/** Resolve a match timestamp to UTC ISO for venue display (print, scorecard, etc.). */
export function resolveMatchDisplayIso(
  match: MatchDisplayDateFields,
  venueTimeZone: string
): string | null {
  const raw = match.scheduleDate ?? match.startDate;
  if (!raw) return null;

  const value = typeof raw === 'string' ? raw : raw.toISOString();
  if (isVenueDatetimeLocal(value)) {
    return fromDatetimeLocalValue(value, venueTimeZone);
  }
  return value;
}
